import React, { useEffect, useState } from 'react';
import { Conteudo, NewBody } from '../../components/Body';
import useStore from '../../services/hooks/useStore';
import { apiV2 } from '../../services/utils';
import { useLocalStore, observer } from 'mobx-react-lite';
import Select from '../../components/Select';
import { ConfiguracaoNFeForm, SectionContainer, SectionTitle, BuscarArquivo, StatusIndicator, StyledSelectWrapper } from './styles';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import X9Logger from '../../services/X9Logger';

interface SelectRow {
  label: string;
  value: string;
}

interface Estado {
  id: number;
  nome: string;
}

interface Cidade {
  id: number;
  nome: string;
  estado_id: number;
}

const ConfiguracaoNFe: React.FC = () => {
  const store = useStore();
  const [estados, setEstados] = useState<Estado[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);

  const state = useLocalStore(() => ({
    sync: false,
    configuracaoId: null as string | null,
    selectedFile: null as File | null,
    fileName: '',
    provedores: [] as any[],
    tipoUnidadeOptions: [
      { label: 'Matriz', value: 'Matriz' },
      { label: 'Filial', value: 'Filial' },
    ],
    tipoEmpresaOptions: [
      { label: 'Microempresa', value: 'ME' },
      { label: 'Empresa Pequeno Porte', value: 'EPP' },
      { label: 'Sem enquadramento', value: 'Sem enquadramento' },
    ],
    regimeTributarioOptions: [
      { label: 'Simples Nacional', value: '1' },
      { label: 'Lucro Presumido', value: '2' },
      { label: 'Lucro Real', value: '3' },
    ],
    ambienteOptions: [
      { label: 'Homologação', value: 'homologacao' },
      { label: 'Produção', value: 'producao' },
    ],
  }));

  const configuracao = useLocalStore(() => ({
    // Dados Cadastrais
    nomeFantasia: '',
    razaoSocial: '',
    inscricaoEstadual: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cep: '',
    estado: null as SelectRow | null,
    cidade: null as SelectRow | null,
    tipoUnidade: null as SelectRow | null,
    tipoEmpresa: null as SelectRow | null,
    regimeTributario: null as SelectRow | null,
    // Configuração NFe
    provedor: null as SelectRow | null,
    ambiente: null as SelectRow | null,
    token: '',
    senhaCertificado: '',
    dataValidade: null as MaterialUiPickersDate,
    serieNfe: '',
    proximoNumero: '',
    naturezaOperacao: '',
    diasAteVencimento: 0,
    certificadoValido: false,
  }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      const validExtensions = ['.pfx', '.p12'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (validExtensions.includes(fileExtension) && file.size <= 5242880) {
        state.selectedFile = file;
        state.fileName = file.name;
      } else {
        store.notificar('Por favor, selecione um arquivo .pfx ou .p12 válido com tamanho até 5MB.');
      }
    }
  };

  const getStatusColor = (dias: number): string => {
    if (dias > 30) return '#00C853'; // Verde
    if (dias > 7) return '#FFC107'; // Amarelo
    return '#F44336'; // Vermelho
  };

  async function carregarProvedores() {
    try {
      const { data } = await apiV2.get('/provedor-nfe/');
      state.provedores = Array.isArray(data) ? data : (data.results || []);
    } catch (error) {
      X9Logger.report(error as Error)
      store.notificar('Erro ao carregar lista de provedores.');
    }
  }

  async function carregarEstadosCidades(): Promise<{ estados: Estado[]; cidades: Cidade[] }> {
    try {
      const { data } = await apiV2.get('/cliente/dados/', {
        params: {
          propriedade: store.propriedade?.id,
        },
      });
      if (data.results) {
        const estadosData = data.results.estados || [];
        const cidadesData = data.results.cidades || [];
        setEstados(estadosData);
        setCidades(cidadesData);
        return { estados: estadosData, cidades: cidadesData };
      }
      return { estados: [], cidades: [] };
    } catch (error) {
      console.error('Erro ao carregar estados e cidades:', error);
      return { estados: [], cidades: [] };
    }
  }

  async function carregarConfiguracao(dadosEstadosCidades?: { estados: Estado[]; cidades: Cidade[] }) {
    try {
      const { data } = await apiV2.get('/configuracao-nfe/', {
        params: {
          prop: store.propriedade?.id,
        },
      });

      const configList = data.results || data || [];
      if (configList && configList.length > 0) {
        const config = configList[0];
        state.configuracaoId = config.uuid || config.id;

        // Dados Cadastrais
        configuracao.nomeFantasia = config.nome_fantasia || store.propriedade?.nome || '';
        configuracao.razaoSocial = config.razao_social || '';
        configuracao.inscricaoEstadual = config.inscricao_estadual || '';
        configuracao.logradouro = config.logadouro || '';
        configuracao.numero = config.numero || '';
        configuracao.bairro = config.bairro || '';
        configuracao.cep = config.cep || '';

        // Estado - usar dados passados como parâmetro primeiro, senão usar store.estados, senão usar estados do state
        if (config.estado) {
          const estadosList = dadosEstadosCidades?.estados || store.estados?.arr || estados;
          const estadoEncontrado = estadosList.find((e: any) => e.id === config.estado);
          if (estadoEncontrado) {
            configuracao.estado = {
              label: estadoEncontrado.nome,
              value: estadoEncontrado.id.toString(),
            };
          }
        }

        // Cidade - usar dados passados como parâmetro primeiro, senão usar cidades do state
        if (config.cidade) {
          const cidadesList = dadosEstadosCidades?.cidades || cidades;
          const cidadeEncontrada = cidadesList.find((c) => c.id === config.cidade);
          if (cidadeEncontrada) {
            configuracao.cidade = {
              label: cidadeEncontrada.nome,
              value: cidadeEncontrada.id.toString(),
            };
          }
        }

        // Tipo Unidade
        if (config.tipo_unidade) {
          configuracao.tipoUnidade = state.tipoUnidadeOptions.find(
            (opt) => opt.value === config.tipo_unidade
          ) || null;
        }

        // Tipo Empresa
        if (config.tipo_empresa) {
          configuracao.tipoEmpresa = state.tipoEmpresaOptions.find(
            (opt) => opt.value === config.tipo_empresa
          ) || null;
        }

        // Regime Tributário
        if (config.regime_tributario) {
          configuracao.regimeTributario = state.regimeTributarioOptions.find(
            (opt) => opt.value === config.regime_tributario.toString()
          ) || null;
        }

        // Configuração NFe
        const provedor = state.provedores.find((p) => p.id === config.provedor_nfe);
        if (provedor) {
          configuracao.provedor = {
            label: provedor.nome,
            value: provedor.id,
          };
        }

        // Ambiente
        if (config.ambiente) {
          configuracao.ambiente = state.ambienteOptions.find(
            (opt) => opt.value === config.ambiente
          ) || null;
        }

        configuracao.token = config.api_token || '';
        configuracao.senhaCertificado = ''; // Não carregar senha por segurança
        configuracao.serieNfe = config.serie_nfe?.toString() || '';
        configuracao.proximoNumero = config.proximo_numero?.toString() || '';
        configuracao.naturezaOperacao = config.natureza_operacao_padrao || '';

        // Data de validade e dias até vencimento
        if (config.certificado_validade) {
          configuracao.dataValidade = new Date(config.certificado_validade);
          const hoje = new Date();
          const validade = new Date(config.certificado_validade);
          const diffTime = validade.getTime() - hoje.getTime();
          configuracao.diasAteVencimento = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }

        configuracao.certificadoValido = config.certificado_valido || false;
      } else {
        // Nova configuração
        configuracao.nomeFantasia = store.propriedade?.nome || '';
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      configuracao.nomeFantasia = store.propriedade?.nome || '';
    }
  }

  async function salvarConfiguracao(): Promise<void> {
    if (!configuracao.provedor || !configuracao.ambiente || !configuracao.token) {
      store.notificar('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Se houver certificado selecionado, validar campos do certificado
    if (state.selectedFile && (!configuracao.senhaCertificado || !configuracao.dataValidade)) {
      store.notificar('Por favor, preencha todos os campos do certificado.');
      return;
    }

    store.toggleLoader();
    try {
      const formData = new FormData();

      // Dados básicos
      formData.append('propriedade', store.propriedade!.id.toString());

      // Dados Cadastrais
      formData.append('nome_fantasia', configuracao.nomeFantasia || '');
      formData.append('razao_social', configuracao.razaoSocial || '');
      formData.append('inscricao_estadual', configuracao.inscricaoEstadual || '');
      formData.append('logadouro', configuracao.logradouro || '');
      formData.append('numero', configuracao.numero || '');
      formData.append('bairro', configuracao.bairro || '');
      formData.append('cep', configuracao.cep || '');

      if (configuracao.estado?.value) {
        formData.append('estado', configuracao.estado.value);
      }
      if (configuracao.cidade?.value) {
        formData.append('cidade', configuracao.cidade.value);
      }
      if (configuracao.tipoUnidade?.value) {
        formData.append('tipo_unidade', configuracao.tipoUnidade.value);
      }
      if (configuracao.tipoEmpresa?.value) {
        formData.append('tipo_empresa', configuracao.tipoEmpresa.value);
      }
      if (configuracao.regimeTributario?.value) {
        formData.append('regime_tributario', configuracao.regimeTributario.value);
      }

      // Configuração NFe
      formData.append('provedor_nfe', configuracao.provedor.value);
      formData.append('ambiente', configuracao.ambiente.value);
      formData.append('api_token', configuracao.token);
      formData.append('serie_nfe', configuracao.serieNfe || '0');
      formData.append('proximo_numero', configuracao.proximoNumero || '0');
      formData.append('natureza_operacao_padrao', configuracao.naturezaOperacao || '');

      // Dados do certificado (se fornecido)
      if (state.selectedFile) {
        formData.append('certificado_digital', state.selectedFile);
        formData.append('senha_certificado', configuracao.senhaCertificado);
        const dataValidadeStr = configuracao.dataValidade!.toISOString().split('T')[0];
        formData.append('certificado_validade', dataValidadeStr);
      }

      let response;
      if (state.configuracaoId) {
        response = await apiV2.patch(`/configuracao-nfe/${state.configuracaoId}/?prop=${store.propriedade?.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await apiV2.post(`/configuracao-nfe/?prop=${store.propriedade?.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        state.configuracaoId = response.data.uuid;
      }

      // Se houver certificado, enviar ao provedor
      if (state.selectedFile && state.configuracaoId) {
        await apiV2.post(`/configuracao-nfe/${state.configuracaoId}/enviar-certificado/?prop=${store.propriedade?.id}`);

        // Recalcular dias até vencimento
        if (configuracao.dataValidade) {
          const hoje = new Date();
          const validade = new Date(configuracao.dataValidade);
          const diffTime = validade.getTime() - hoje.getTime();
          configuracao.diasAteVencimento = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
      }

      store.notificar('Configuração salva com sucesso!');

      // Limpar campos do certificado após salvar
      if (state.selectedFile) {
        state.selectedFile = null;
        state.fileName = '';
        configuracao.senhaCertificado = '';
      }
    } catch (error) {
      X9Logger.report(error as Error);
      store.notificar('Erro ao salvar configuração. Tente novamente.');
    } finally {
      store.toggleLoader();
    }
  }


  async function sincronizar(): Promise<void> {
    store.toggleLoader();
    try {
      await carregarProvedores()
      const dadosEstadosCidades =  await carregarEstadosCidades()
      
      await carregarConfiguracao(dadosEstadosCidades);
    } catch (error) {
      X9Logger.report(error as Error);
    } finally {
      store.toggleLoader();
      state.sync = true;
    }
  }

  useEffect(() => {
    if (store.propriedade) {
      sincronizar();
    }
    // eslint-disable-next-line
  }, [store.propriedade]);

  useEffect(() => {
    store.setTitulo('Configuração da Emissão de NFe');
    // eslint-disable-next-line
  }, []);

  const provedorRows: SelectRow[] = state.provedores.map((provedor) => ({
    label: provedor.nome,
    value: provedor.id,
  }));

  return state.sync ? (
    <NewBody>
      <Conteudo>
        <Grid container spacing={4} style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
          {/* Coluna Esquerda: Dados da Propriedade */}
          <Grid item xs={12} lg={6} style={{ padding: '0 15px' }}>
            <Grid container spacing={1}>
              <Grid item xs={12} style={{ padding: '0 0 8px 0' }}>
                <SectionTitle>Dados da Propriedade</SectionTitle>
              </Grid>
              <Grid item xs={12}>
                <ConfiguracaoNFeForm>
                  <Grid container spacing={1}>
                    <Grid item xs={12} style={{ paddingTop: '0px' }}>
                      <TextField
                        value={configuracao.nomeFantasia}
                        onChange={(e) => (configuracao.nomeFantasia = e.target.value)}
                        label="Nome Fantasia"
                        placeholder="Nome da sua Propriedade"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        value={configuracao.razaoSocial}
                        onChange={(e) => (configuracao.razaoSocial = e.target.value)}
                        label="Razão Social"
                        placeholder="Informe a razão social"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        value={configuracao.inscricaoEstadual}
                        onChange={(e) => (configuracao.inscricaoEstadual = e.target.value)}
                        label="Inscrição Estadual"
                        placeholder="Informe a Inscrição Estadual"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        value={configuracao.logradouro}
                        onChange={(e) => (configuracao.logradouro = e.target.value)}
                        label="Logradouro/Endereço"
                        placeholder="Informe Endereço"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        value={configuracao.numero}
                        onChange={(e) => (configuracao.numero = e.target.value)}
                        label="Número"
                        placeholder="Informe o Número"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        value={configuracao.cep}
                        onChange={(e) => (configuracao.cep = e.target.value)}
                        label="CEP"
                        placeholder="Informe o CEP"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        value={configuracao.bairro}
                        onChange={(e) => (configuracao.bairro = e.target.value)}
                        label="Bairro"
                        placeholder="Informe o Bairro"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StyledSelectWrapper>
                        <Select
                          value={configuracao.estado}
                          placeholder="Selecione o Estado"
                          onChange={(e: any) => {
                            configuracao.cidade = null;
                            configuracao.estado = e;
                          }}
                          options={(estados.length > 0 ? estados : (store.estados?.arr || [])).map((estado: any) => ({
                            label: estado.nome,
                            value: estado.id.toString(),
                          }))}
                        />
                      </StyledSelectWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <StyledSelectWrapper>
                        <Select
                          disabled={!configuracao.estado}
                          value={configuracao.cidade}
                          placeholder="Selecione a Cidade"
                          onChange={(e: any) => (configuracao.cidade = e)}
                          options={cidades
                            .filter((cidade) => configuracao.estado && cidade.estado_id === parseInt(configuracao.estado.value))
                            .sort((a, b) => (a.nome > b.nome ? 1 : b.nome > a.nome ? -1 : 0))
                            .map((cidade) => ({
                              label: cidade.nome,
                              value: cidade.id.toString(),
                            }))}
                        />
                      </StyledSelectWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <StyledSelectWrapper>
                        <Select
                          value={configuracao.tipoUnidade}
                          placeholder="Selecione o Tipo da Unidade"
                          onChange={(e: any) => (configuracao.tipoUnidade = e)}
                          options={state.tipoUnidadeOptions}
                        />
                      </StyledSelectWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <StyledSelectWrapper>
                        <Select
                          value={configuracao.tipoEmpresa}
                          placeholder="Selecione o Tipo da Empresa"
                          onChange={(e: any) => (configuracao.tipoEmpresa = e)}
                          options={state.tipoEmpresaOptions}
                        />
                      </StyledSelectWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <StyledSelectWrapper>
                        <Select
                          value={configuracao.regimeTributario}
                          placeholder="Selecione o Regime Tributário"
                          onChange={(e: any) => (configuracao.regimeTributario = e)}
                          options={state.regimeTributarioOptions}
                        />
                      </StyledSelectWrapper>
                    </Grid>
                  </Grid>
                </ConfiguracaoNFeForm>
              </Grid>
            </Grid>
          </Grid>

          {/* Coluna Direita: Configurações da NFe e Certificado Digital */}
          <Grid item xs={12} lg={6} style={{ padding: '0 15px' }}>
            <Grid container spacing={2}>
              {/* Seção: Certificado Digital */}
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ padding: '0 0 8px 0' }}>
                    <SectionTitle>Certificado Digital</SectionTitle>
                  </Grid>
                  <Grid item xs={12}>
                    <SectionContainer>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2" style={{ marginBottom: '10px', fontSize: '14px' }}>
                            Certificado Digital A1 (pfx / p2)
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            component="label"
                            style={{
                              width: '100%',
                              textTransform: 'none',
                              backgroundColor: '#049CE7',
                              color: 'white',
                              height: '48px',
                              fontSize: '16px',
                              borderRadius: '4px',
                              fontWeight: '500',
                              padding: '12px 24px',
                            }}
                          >
                            Buscar Arquivo
                            <BuscarArquivo type="file" accept=".pfx,.p12" onChange={handleFileChange} />
                          </Button>
                          {state.fileName && (
                            <Typography variant="caption" style={{ display: 'block', marginTop: '5px' }}>
                              {state.fileName}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            type="text"
                            value={configuracao.senhaCertificado}
                            onChange={(e) => (configuracao.senhaCertificado = e.target.value)}
                            label="Senha do Certificado"
                            placeholder="Informe a Senha"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <div style={{ width: '100%' }}>
                            <KeyboardDatePicker
                              autoOk
                              disableToolbar
                              variant="inline"
                              format="dd/MM/yyyy"
                              margin="normal"
                              label="Data Validade Certificado"
                              placeholder="Data da Validade"
                              value={configuracao.dataValidade}
                              onChange={(date) => (configuracao.dataValidade = date)}
                              style={{ width: '100%' }}
                              inputProps={{
                                style: { height: '40px', fontSize: '14px', padding: '10px 14px' }
                              }}
                            />
                          </div>
                        </Grid>
                        {configuracao.diasAteVencimento > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                              Dias para renovar: {configuracao.diasAteVencimento}
                              <StatusIndicator color={getStatusColor(configuracao.diasAteVencimento)} />
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </SectionContainer>
                  </Grid>
                </Grid>
              </Grid>
              {/* Seção: Configurações da NFe */}
              <Grid item xs={12} style={{ paddingTop: '0px' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ padding: '0 0 8px 0' }}>
                    <SectionTitle>Configurações da NFe</SectionTitle>
                  </Grid>
                  <Grid item xs={12}>
                    <SectionContainer>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TextField
                            value={configuracao.token}
                            onChange={(e) => (configuracao.token = e.target.value)}
                            label="Informe o Token"
                            placeholder="Informe o Token"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <StyledSelectWrapper>
                            <Select
                              value={configuracao.provedor}
                              placeholder="Selecione o Provedor"
                              onChange={(e: any) => (configuracao.provedor = e)}
                              options={provedorRows}
                            />
                          </StyledSelectWrapper>
                        </Grid>
                        <Grid item xs={6}>
                          <StyledSelectWrapper>
                            <Select
                              value={configuracao.ambiente}
                              placeholder="Selecione o Ambiente"
                              onChange={(e: any) => (configuracao.ambiente = e)}
                              options={state.ambienteOptions}
                            />
                          </StyledSelectWrapper>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            value={configuracao.serieNfe}
                            onChange={(e) => (configuracao.serieNfe = e.target.value)}
                            label="Série da NFe"
                            placeholder="Informe a série da NFe"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            value={configuracao.proximoNumero}
                            onChange={(e) => (configuracao.proximoNumero = e.target.value)}
                            label="Próximo Número"
                            placeholder="Informe o próximo número da NFe"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            value={configuracao.naturezaOperacao}
                            onChange={(e) => (configuracao.naturezaOperacao = e.target.value)}
                            label="Natureza da Operação Padrão"
                            placeholder="Informe a Natureza da Operação"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </SectionContainer>
                  </Grid>
                </Grid>
              </Grid>



              {/* Botão Salvar */}
              <Grid item xs={12} style={{ marginTop: '20px' }}>
                <Button
                  onClick={salvarConfiguracao}
                  variant="contained"
                  style={{
                    backgroundColor: '#00C853',
                    textTransform: 'none',
                    width: '100%',
                    borderRadius: '4px',
                    height: '40px',
                    fontSize: '14px',
                  }}
                >
                  Salvar Configurações
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Conteudo>
    </NewBody>
  ) : null;
};

export default observer(ConfiguracaoNFe);

